import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  usePersonalisedProfile,
  useUpdatePersonalisedProfile,
} from "@/hooks/usePersonalisedProfile";
import { ProfileBanner } from "@/components/profile/ProfileBanner";
import { PersonalisePanel } from "@/components/profile/PersonalisePanel";
import { ProgressStrip } from "@/components/profile/ProgressStrip";
import { StatsFooter } from "@/components/profile/StatsFooter";
import { HeroArtifactCard } from "@/components/profile/HeroArtifactCard";
import { StoryBadgesRow } from "@/components/profile/StoryBadgesRow";
import { HighlightsPanel } from "@/components/profile/HighlightsPanel";
import { LoadoutStrip } from "@/components/profile/LoadoutStrip";
import {
  FOCUS_TAGLINE_PRESETS,
  MICRO_MANTRA_PRESETS,
  inferPatternFromArc,
} from "../../shared/profilePresets";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Pencil, Sparkles } from "lucide-react";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function Profile() {
  const { user } = useAuth();
  const { data: profileData, isLoading, isError } = usePersonalisedProfile();
  const updateProfile = useUpdatePersonalisedProfile();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);

  const profile = profileData as any;
  const totalSessions: number | null = profile?.totalSessions ?? null;
  const projectsCompleted: number | null = profile?.projectsCompleted ?? null;

  const [editDisplayName, setEditDisplayName] = useState("");
  const [editRoleTitle, setEditRoleTitle] = useState("");
  const [editCompanyOrContext, setEditCompanyOrContext] = useState("");
  const [editFocusPresetId, setEditFocusPresetId] = useState<string>("");
  const [editFocusCustom, setEditFocusCustom] = useState("");
  const [editMantraPresetId, setEditMantraPresetId] = useState<string>("");
  const [editMantraCustom, setEditMantraCustom] = useState("");

  function openEdit() {
    if (!profile) return;
    setEditDisplayName(profile.displayName || "");
    setEditRoleTitle(profile.roleTitle || "");
    setEditCompanyOrContext(profile.companyOrContext || "");
    setEditFocusPresetId(profile.focusTaglinePresetId || "");
    setEditFocusCustom(profile.focusTaglineCustom || "");
    setEditMantraPresetId(profile.microMantraPresetId || "");
    setEditMantraCustom(profile.microMantraCustom || "");
    setEditOpen(true);
  }

  function handleSaveEdit() {
    const updates: Record<string, any> = {};
    if (editDisplayName && editDisplayName !== profile?.displayName)
      updates.displayName = editDisplayName;
    if (editRoleTitle !== (profile?.roleTitle || ""))
      updates.roleTitle = editRoleTitle || null;
    if (editCompanyOrContext !== (profile?.companyOrContext || ""))
      updates.companyOrContext = editCompanyOrContext || null;
    if (editFocusPresetId !== (profile?.focusTaglinePresetId || ""))
      updates.focusTaglinePresetId = editFocusPresetId || null;
    if (editFocusCustom !== (profile?.focusTaglineCustom || ""))
      updates.focusTaglineCustom = editFocusCustom || null;
    if (editMantraPresetId !== (profile?.microMantraPresetId || ""))
      updates.microMantraPresetId = editMantraPresetId || null;
    if (editMantraCustom !== (profile?.microMantraCustom || ""))
      updates.microMantraCustom = editMantraCustom || null;

    if (Object.keys(updates).length === 0) {
      setEditOpen(false);
      return;
    }

    updateProfile.mutate(updates, {
      onSuccess: () => {
        toast({ title: "Profile updated" });
        setEditOpen(false);
      },
      onError: () => {
        toast({
          title: "Failed to update profile",
          variant: "destructive",
        });
      },
    });
  }

  function handleThemeColor(color: string) {
    updateProfile.mutate({ themeColor: color as any });
  }

  function handleBannerPattern(patternId: string) {
    updateProfile.mutate({
      bannerPatternId: patternId,
      bannerAccentMode: "custom" as any,
    });
  }

  function handleResetToAuto() {
    updateProfile.mutate({
      bannerPatternId: null,
      bannerAccentMode: "auto_by_arc" as any,
    });
  }

  const resolvedFocusTagline = profile?.focusTaglinePresetId
    ? FOCUS_TAGLINE_PRESETS[profile.focusTaglinePresetId]
    : profile?.focusTaglineCustom || null;

  const resolvedMantra = profile?.microMantraPresetId
    ? MICRO_MANTRA_PRESETS[profile.microMantraPresetId]
    : profile?.microMantraCustom || null;

  const resolvedBannerPattern =
    profile?.bannerPatternId ||
    (profile?.bannerAccentMode === "auto_by_arc"
      ? inferPatternFromArc(null)
      : null);

  const streakActive =
    (profile?.lastComputedStreakDays ?? 0) >= 3 && (profile?.streakFlairEnabled !== false);

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="profile-loading">
        <Skeleton className="h-28 w-full rounded-xl" />
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="max-w-2xl mx-auto p-4" data-testid="profile-error">
        <p className="text-destructive">
          Failed to load profile. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="profile-page">
      {/* Banner */}
      <ProfileBanner
        themeColor={profile.themeColor}
        bannerPatternId={resolvedBannerPattern}
        streakActive={streakActive}
      />

      {/* Hero / Identity */}
      <div className="-mt-8 px-4 relative z-10">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 border-4 border-background" data-testid="profile-avatar">
            <AvatarImage src={user?.profileImageUrl || undefined} />
            <AvatarFallback className="text-lg font-semibold">
              {getInitials(profile.displayName)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 pt-6">
            <div className="flex items-center gap-2">
              <h2
                className="text-xl font-bold"
                data-testid="profile-display-name"
              >
                {profile.displayName}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={openEdit}
                data-testid="edit-profile-btn"
                className="h-7 px-2"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>

            {profile.roleTitle && (
              <p
                className="text-sm text-muted-foreground"
                data-testid="profile-role"
              >
                {profile.roleTitle}
              </p>
            )}
            {profile.companyOrContext && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="profile-company"
              >
                {profile.companyOrContext}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-1.5">
          <div className="flex items-center gap-2" data-testid="focus-tagline">
            <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm">
              {resolvedFocusTagline ? (
                <>
                  <span className="text-muted-foreground">Working on: </span>
                  <span className="font-medium">{resolvedFocusTagline}</span>
                </>
              ) : (
                <button
                  onClick={openEdit}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="select-focus-prompt"
                >
                  Working on: Select a focus
                </button>
              )}
            </span>
          </div>

          {resolvedMantra ? (
            <p
              className="text-xs text-muted-foreground italic pl-5"
              data-testid="micro-mantra"
            >
              Mantra: {resolvedMantra}
            </p>
          ) : (
            <button
              onClick={openEdit}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors italic pl-5"
              data-testid="select-mantra-prompt"
            >
              Mantra: Select a mantra
            </button>
          )}
        </div>
      </div>

      {/* Batch 4 — Progress Strip */}
      <div className="px-1">
        <ProgressStrip
          repsWeek={profile.lastComputedRepsWeek ?? null}
          streakDays={profile.lastComputedStreakDays ?? null}
          arcCompletion={profile.lastComputedArcCompletion ?? null}
          arcTitle={profile.lastComputedArcTitle ?? null}
        />
      </div>

      {/* Batch 5 — Narrative Layer */}
      <div className="rounded-xl border bg-card p-4 space-y-5">
        <HeroArtifactCard
          artifactId={profile.heroArtifactId}
          onUpdate={() => {}}
        />
        <StoryBadgesRow selectedBadgeIds={profile.storyBadgeIds ?? []} />
        <HighlightsPanel selectedIds={profile.highlightCardIds ?? []} />
      </div>

      {/* Batch 6 — Loadout Strip */}
      <div className="rounded-xl border bg-card p-4">
        <LoadoutStrip selectedIds={profile.loadoutAgentPresetIds ?? []} />
      </div>

      {/* Personalise Panel (theme + banner) */}
      <PersonalisePanel
        themeColor={profile.themeColor}
        bannerPatternId={profile.bannerPatternId}
        bannerAccentMode={profile.bannerAccentMode}
        onUpdateThemeColor={handleThemeColor}
        onUpdateBannerPattern={handleBannerPattern}
        onResetToAuto={handleResetToAuto}
      />

      {/* Batch 4 — Stats Footer */}
      <StatsFooter
        joinedAt={(user as any)?.createdAt}
        totalSessions={totalSessions}
        projectsCompleted={projectsCompleted}
      />

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-display-name">Display Name</Label>
              <Input
                id="edit-display-name"
                data-testid="input-display-name"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                maxLength={255}
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Role Title</Label>
              <Input
                id="edit-role"
                data-testid="input-role-title"
                value={editRoleTitle}
                onChange={(e) => setEditRoleTitle(e.target.value)}
                placeholder="e.g. Product Lead"
                maxLength={100}
              />
            </div>
            <div>
              <Label htmlFor="edit-company">Company / Context</Label>
              <Input
                id="edit-company"
                data-testid="input-company-context"
                value={editCompanyOrContext}
                onChange={(e) => setEditCompanyOrContext(e.target.value)}
                placeholder="e.g. Indie founder"
                maxLength={100}
              />
            </div>

            <div>
              <Label className="mb-2 block">Focus Tagline</Label>
              <RadioGroup
                value={editFocusPresetId}
                onValueChange={(v) => {
                  setEditFocusPresetId(v);
                  setEditFocusCustom("");
                }}
                data-testid="focus-tagline-presets"
              >
                {Object.entries(FOCUS_TAGLINE_PRESETS).map(([id, label]) => (
                  <div key={id} className="flex items-center gap-2">
                    <RadioGroupItem value={id} id={`focus-${id}`} />
                    <Label htmlFor={`focus-${id}`} className="text-sm font-normal cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <div className="mt-2">
                <Label htmlFor="focus-custom" className="text-xs text-muted-foreground">
                  Or write your own (60 chars max)
                </Label>
                <Input
                  id="focus-custom"
                  data-testid="input-focus-custom"
                  value={editFocusCustom}
                  onChange={(e) => {
                    setEditFocusCustom(e.target.value);
                    if (e.target.value) setEditFocusPresetId("");
                  }}
                  maxLength={60}
                  placeholder="Custom focus tagline..."
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Micro Mantra</Label>
              <RadioGroup
                value={editMantraPresetId}
                onValueChange={(v) => {
                  setEditMantraPresetId(v);
                  setEditMantraCustom("");
                }}
                data-testid="mantra-presets"
              >
                {Object.entries(MICRO_MANTRA_PRESETS).map(([id, label]) => (
                  <div key={id} className="flex items-center gap-2">
                    <RadioGroupItem value={id} id={`mantra-${id}`} />
                    <Label htmlFor={`mantra-${id}`} className="text-sm font-normal cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <div className="mt-2">
                <Label htmlFor="mantra-custom" className="text-xs text-muted-foreground">
                  Or write your own (60 chars max)
                </Label>
                <Input
                  id="mantra-custom"
                  data-testid="input-mantra-custom"
                  value={editMantraCustom}
                  onChange={(e) => {
                    setEditMantraCustom(e.target.value);
                    if (e.target.value) setEditMantraPresetId("");
                  }}
                  maxLength={60}
                  placeholder="Custom mantra..."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              data-testid="cancel-edit-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateProfile.isPending}
              data-testid="save-profile-btn"
            >
              {updateProfile.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
